import { useCallback, useMemo, useState } from 'react';
import type { SelectedEntity } from '../components/EntityFilterSelect';
import type { FilterValue } from '../components/schedule/ScheduleFilters';
import { EMPTY_FILTERS } from '../lib/scheduleFilters';
import type { SavedFilterDto } from '../lib/types';

function zip(ids: number[], labels: string[]): SelectedEntity[] {
  return ids.map((id, i) => ({ id, label: labels[i] ?? String(id) }));
}

function sameIds(a: SelectedEntity[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const ids = new Set(a.map((x) => x.id));
  return b.every((id) => ids.has(id));
}

/**
 * Filter state shared by the three filtered views. The filter bar is the single source of
 * truth for which workers / projects are in scope: applying a saved filter loads its members
 * into the bar rather than filtering in parallel with it, so there's one place to look and one
 * place to edit. Hand-editing the selection afterwards drops the "applied" badge, since the
 * selection no longer matches what was saved.
 */
export function useScheduleFilterState(onSelectionChange?: () => void) {
  const [filters, setFilters] = useState<FilterValue>(EMPTY_FILTERS);
  const [appliedFilter, setAppliedFilter] = useState<SavedFilterDto | null>(null);

  const changeFilters = useCallback(
    (next: FilterValue) => {
      setFilters(next);
      setAppliedFilter((applied) => {
        if (!applied) return null;
        const stillMatches =
          sameIds(next.people, applied.personIds) && sameIds(next.projects, applied.projectIds);
        return stillMatches ? applied : null;
      });
      onSelectionChange?.();
    },
    [onSelectionChange],
  );

  const applySavedFilter = useCallback(
    (filter: SavedFilterDto | null) => {
      setAppliedFilter(filter);
      setFilters((prev) => ({
        ...prev,
        people: filter ? zip(filter.personIds, filter.personNames) : [],
        projects: filter ? zip(filter.projectIds, filter.projectNames) : [],
      }));
      onSelectionChange?.();
    },
    [onSelectionChange],
  );

  // Stable references so they don't retrigger the grid queries on every render.
  const personIds = useMemo(
    () => (filters.people.length > 0 ? filters.people.map((p) => p.id) : undefined),
    [filters.people],
  );
  const projectIds = useMemo(
    () => (filters.projects.length > 0 ? filters.projects.map((p) => p.id) : undefined),
    [filters.projects],
  );

  return { filters, changeFilters, appliedFilter, applySavedFilter, personIds, projectIds };
}
